import yaml from 'js-yaml';
import { marked } from 'marked';

// Every .md file under content/pages/ is bundled into the client at build
// time (eager + ?raw) rather than fetched at runtime - there's no content
// service to fetch from (see the go-live plan's D2: content-as-code, not a
// database-backed CMS), and this works identically in the browser and
// during prerendering (which runs this same module in Node - see
// react-router.config.js's prerender list).
//
// Files are ordinary `---`-delimited YAML front matter over a Markdown
// body - the format the Front Matter VS Code extension
// (https://frontmatter.codes) reads and writes directly, so editing a page
// doesn't require touching this file or any route module.
//
// listPages()/getPage() are the only way any route reads content (see
// routes/help.jsx etc.) - if a database-backed services/content ever
// replaces this file, it only needs to implement the same two functions;
// nothing that calls them needs to change.
const rawPages = import.meta.glob('./pages/*.md', { eager: true, query: '?raw', import: 'default' });

const FRONT_MATTER_PATTERN = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/;

function parsePage(slug, raw) {
  const match = FRONT_MATTER_PATTERN.exec(raw);
  if (!match) {
    throw new Error(`content/pages/${slug}.md is missing its --- front matter block.`);
  }
  const [, frontMatterYaml, body] = match;
  const frontMatter = yaml.load(frontMatterYaml) || {};
  // YAML auto-detects an unquoted ISO-date-shaped scalar (e.g. `updated:
  // 2026-08-18`) as a real Date, not a string - harmless to js-yaml, but
  // React refuses to render a Date as a child. The Front Matter VS Code
  // extension's date fields write exactly that shape, so this normalizes
  // rather than relying on every edit staying quoted.
  if (frontMatter.updated instanceof Date) {
    frontMatter.updated = frontMatter.updated.toISOString().slice(0, 10);
  }
  return { ...frontMatter, html: marked.parse(body.trim()) };
}

const pagesBySlug = new Map();
for (const [path, raw] of Object.entries(rawPages)) {
  const slug = path.replace('./pages/', '').replace(/\.md$/, '');
  pagesBySlug.set(slug, parsePage(slug, raw));
}

export function listPages() {
  return [...pagesBySlug.entries()].map(([slug, page]) => ({
    slug,
    title: page.title,
    description: page.description
  }));
}

export function getPage(slug) {
  return pagesBySlug.get(slug) || null;
}
